import React, { useState, useEffect, useCallback } from "react";
import PageHeader from "../components/layout/PageHeader";
import { usePageTitle } from "../contexts/PageTitleContext";
import { useAuth } from "../contexts/AuthContext";
import { useParams } from "react-router-dom";
import { useWsNavigate } from "../hooks/useWsNavigate";
import { personaAPI, voiceConfigAPI } from "../services/api";
import { TTS_PROVIDERS } from "../constants/providerCatalogue";
import { Play, Pause, Eye, ChevronDown, ChevronUp } from "lucide-react";
import { useCan } from "../hooks/useCan";
import { usePostHog } from "@posthog/react";

// ── Constants ─────────────────────────────────────────────────────────────────

const INTERRUPTION_LEVEL_OPTIONS = [
  { value: "", label: "Select level" },
  { value: "off", label: "Off — never interrupts" },
  { value: "low", label: "Low — after 10s of agent speech" },
  { value: "med", label: "Medium — after 6s of agent speech" },
  { value: "high", label: "High — after 3s of agent speech" },
];

const VOICE_CLARITY_OPTIONS = [
  { value: "", label: "Select clarity" },
  { value: "clear", label: "Clear" },
  { value: "muffled", label: "Muffled" },
  { value: "noisy", label: "Noisy" },
  { value: "garbled", label: "Garbled" },
  { value: "unintelligible", label: "Unintelligible" },
];

// Background noise volume, percent. Unset = the platform default (python_tools
// DEFAULT_NOISE_VOLUME, 10%). Capped at 30 — louder noise makes agents' VAD
// treat it as the caller talking. Mirrors Persona::BACKGROUND_VOLUME_RANGE.
const BACKGROUND_VOLUME_MIN = 1;
const BACKGROUND_VOLUME_MAX = 30;
const BACKGROUND_VOLUME_DEFAULT = 10;

const NETWORK_SIMULATION_OPTIONS = [
  { value: "", label: "Select connection quality" },
  { value: "stable", label: "Stable — no network issues" },
  { value: "unstable", label: "Unstable — 5% loss, 200ms latency, 80ms jitter" },
  { value: "poor", label: "Poor — 15% loss, 400ms latency, 150ms jitter" },
];

const INPUT_CLS =
  "w-full px-3 py-2 border border-input bg-background rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black";
const SELECT_CLS = INPUT_CLS;

// ── Voice preview (single-instance audio) ─────────────────────────────────────

let activeAudio = null;

const VoicePreviewButton = ({ url }) => {
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    return () => {
      if (activeAudio) { activeAudio.pause(); activeAudio = null; }
      setPlaying(false);
    };
  }, [url]);

  if (!url) return null;

  const toggle = () => {
    if (playing && activeAudio) {
      activeAudio.pause(); activeAudio = null; setPlaying(false); return;
    }
    if (activeAudio) { activeAudio.pause(); activeAudio = null; }
    const audio = new Audio(url);
    audio.addEventListener("ended", () => setPlaying(false));
    audio.addEventListener("error", () => setPlaying(false));
    audio.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    activeAudio = audio;
  };

  return (
    <button
      type="button" onClick={toggle}
      title={playing ? "Stop preview" : "Play preview"}
      className="shrink-0 h-9 w-9 flex items-center justify-center rounded-lg border border-input text-foreground hover:bg-muted transition-colors"
    >
      {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
    </button>
  );
};

// ── ConfigField (reuses same pattern as CreateAgent) ──────────────────────────

const ConfigField = ({ field, value, onChange, voiceCatalog, onLoadVoiceCatalog, langCode }) => {
  useEffect(() => {
    if (field.type === "select_from_api" && field.api_source && onLoadVoiceCatalog) {
      onLoadVoiceCatalog(field.api_source);
    }
  }, [field.type, field.api_source, onLoadVoiceCatalog]);

  if (field.type === "boolean") {
    return (
      <label className="flex items-center gap-2.5 cursor-pointer mt-1">
        <div
          onClick={() => onChange(!value)}
          className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${value ? "bg-gray-900" : "bg-gray-300"}`}
        >
          <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-card rounded-full shadow transition-transform ${value ? "translate-x-5" : "translate-x-0"}`} />
        </div>
        {field.description && <span className="text-sm text-muted-foreground">{field.description}</span>}
      </label>
    );
  }

  if (field.type === "select" || field.type === "select_from_api") {
    if (field.type === "select_from_api") {
      const catalog = voiceCatalog || { status: "loading", voices: [] };
      const knownSource = field.api_source === "elevenlabs_voices" || field.api_source === "cartesia_voices";

      if (!knownSource || catalog.status === "error") {
        return (
          <div className="space-y-1">
            <input type="text" value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={INPUT_CLS} placeholder={field.description || "Enter ID..."} />
            {catalog.status === "error" && <p className="text-xs text-amber-600">Couldn't load voice list. Enter the ID manually.</p>}
          </div>
        );
      }
      if (catalog.status === "loading") {
        return <div className={`${SELECT_CLS} bg-muted text-muted-foreground flex items-center`}>Loading voices…</div>;
      }

      const allVoices = catalog.voices || [];
      // Filter by the persona's primary language when the voice objects carry a
      // language tag (Cartesia tags every voice; ElevenLabs labels are sparse).
      // Fall back to the full list if the filter would empty the dropdown.
      let voices = allVoices;
      if (langCode) {
        const filtered = allVoices.filter((v) => (v.language || "").toLowerCase().startsWith(langCode));
        if (filtered.length) voices = filtered;
      }
      const selectedVoice = allVoices.find((v) => v.id === value);
      return (
        <div className="flex items-center gap-2">
          <select value={value ?? ""} onChange={(e) => onChange(e.target.value || null)} className={SELECT_CLS}>
            <option value="">— Select voice —</option>
            {voices.map((v) => {
              const label = [v.name, v.language, v.gender].filter(Boolean).join(" · ");
              return <option key={v.id} value={v.id}>{label || v.id}</option>;
            })}
          </select>
          <VoicePreviewButton url={selectedVoice?.preview_url} />
        </div>
      );
    }

    // Filter fixed-option selects (e.g. Azure voices, Cartesia/ElevenLabs
    // models) by the selected language via language_map — same as the agent form.
    let options = field.options || [];
    if (field.language_dependent && field.language_map && langCode) {
      const allowed = field.language_map[langCode] || field.language_map["_default"] || null;
      if (allowed) options = options.filter((o) => allowed.includes(o.value));
    }
    return (
      <select value={value ?? field.default ?? ""} onChange={(e) => onChange(e.target.value)} className={SELECT_CLS}>
        {options.map((opt) => <option key={String(opt.value)} value={opt.value ?? ""}>{opt.label}</option>)}
      </select>
    );
  }

  if (field.type === "float" || field.type === "int") {
    const current = value ?? field.default ?? field.min ?? 0;
    return (
      <div className="flex items-center gap-3">
        <input type="range" min={field.min} max={field.max} step={field.step || 1} value={current}
          onChange={(e) => onChange(field.type === "int" ? parseInt(e.target.value, 10) : parseFloat(e.target.value))}
          className="flex-1 accent-gray-900" />
        <span className="text-sm font-mono w-12 text-right text-foreground">{current}</span>
      </div>
    );
  }

  return <input type="text" value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={INPUT_CLS} placeholder={field.description || ""} />;
};

// ── Main component ────────────────────────────────────────────────────────────

const CreatePersona = () => {
  const { currentWorkspace, access } = useAuth();
  const navigate = useWsNavigate();
  const { id: editId } = useParams();
  const posthog = usePostHog();
  const isEditMode = !!editId;
  const canWrite = useCan("persona", "write");
  const isInternalAdmin = !!access?.internal_admin;
  const { setPageTitle, setParentPage, setShowBackButton } = usePageTitle();

  // Predefined (global) personas are system-managed: only internal admins may
  // edit them. Everyone else — even with persona-write — sees them read-only.
  // New personas (create mode) are custom, so default to custom = editable.
  const [personaIsCustom, setPersonaIsCustom] = useState(true);
  const editable = canWrite && (personaIsCustom || isInternalAdmin);
  const viewOnly = isEditMode && !editable;

  useEffect(() => {
    setPageTitle(!isEditMode ? "Create Persona" : editable ? "Edit Persona" : "View Persona");
    setParentPage({ name: "Persona Library", href: "/live-simulations/persona" });
    setShowBackButton(true);
    return () => { setPageTitle(null); setParentPage(null); setShowBackButton(false); };
  }, [isEditMode, editable, setPageTitle, setParentPage, setShowBackButton]);

  const [form, setForm] = useState({
    name: "",
    description: "",
    prompt_instructions: "",
    language_id: "", // primary language — drives the custom-voice dropdown filter
    voice_mode: "default", // "default" = inherit from agent, "custom" = pick provider + voice
    tts_provider: "openai",
    tts_config: {},
    settings: {},
  });
  const [languages, setLanguages] = useState([]);
  const [isVoiceSettingsOpen, setIsVoiceSettingsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingPersona, setLoadingPersona] = useState(false);
  const [backgroundNoises, setBackgroundNoises] = useState([]);

  // Voice catalog cache (same pattern as CreateAgent)
  const [voiceCatalogs, setVoiceCatalogs] = useState({});
  const loadVoiceCatalog = useCallback((apiSource) => {
    if (!apiSource) return;
    setVoiceCatalogs((prev) => {
      if (prev[apiSource]) return prev;
      return { ...prev, [apiSource]: { status: "loading", voices: [] } };
    });
    const fetcher =
      apiSource === "elevenlabs_voices" ? voiceConfigAPI.getElevenLabsVoices :
      apiSource === "cartesia_voices" ? voiceConfigAPI.getCartesiaVoices : null;
    if (!fetcher) return;
    fetcher()
      .then((resp) => {
        const voices = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : [];
        setVoiceCatalogs((prev) => ({ ...prev, [apiSource]: { status: "ready", voices } }));
      })
      .catch((err) => {
        setVoiceCatalogs((prev) => ({ ...prev, [apiSource]: { status: "error", voices: [], error: err.message } }));
      });
  }, []);

  // Fetch background noises on mount
  useEffect(() => {
    voiceConfigAPI.getBackgroundNoises()
      .then((data) => setBackgroundNoises(data.data || []))
      .catch(() => setBackgroundNoises([]));
  }, []);

  // Fetch languages on mount (same source as the agent form). New personas
  // default to English (US) — only set when nothing is chosen yet so we never
  // clobber a value loaded in edit mode.
  useEffect(() => {
    voiceConfigAPI.getAllLanguages()
      .then((r) => {
        const langs = r.data || [];
        setLanguages(langs);
        if (!isEditMode) {
          const enUs = langs.find((l) => ["en-us", "en"].includes(l.language_key?.toLowerCase()));
          if (enUs) setForm((f) => (f.language_id ? f : { ...f, language_id: enUs.id }));
        }
      })
      .catch(() => setLanguages([]));
  }, [isEditMode]);

  // Fetch persona for edit mode
  useEffect(() => {
    if (!editId || !currentWorkspace?.id) return;
    setLoadingPersona(true);
    personaAPI.getPersona(editId, currentWorkspace.id)
      .then((data) => {
        const p = data.data;
        const s = p.settings || {};
        setPersonaIsCustom(!!p.is_custom);
        setForm({
          name: p.name || "",
          description: p.description || "",
          prompt_instructions: p.prompt_instructions || "",
          language_id: p.language_id || "",
          voice_mode: s.tts_provider ? "custom" : "default",
          tts_provider: s.tts_provider || "openai",
          tts_config: s.tts_config || {},
          settings: s,
        });
        // Auto-expand the (collapsed-by-default) Voice settings section when the
        // persona already has acoustic settings, so saved values are visible
        // instead of looking like they reverted.
        const hasVoiceSettings = ["interruption_level", "voice_clarity_preset", "background_sound", "background_sound_volume", "network_simulation_preset", "speed"]
          .some((k) => s[k] !== undefined && s[k] !== "" && s[k] !== null);
        if (hasVoiceSettings) setIsVoiceSettingsOpen(true);
      })
      .catch(() => setError("Failed to load persona for editing."))
      .finally(() => setLoadingPersona(false));
  }, [editId, currentWorkspace?.id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSettingChange = (key, value) => {
    setForm((prev) => ({ ...prev, settings: { ...prev.settings, [key]: value } }));
  };

  const handleTtsConfigChange = (key, value) => {
    setForm((prev) => ({ ...prev, tts_config: { ...prev.tts_config, [key]: value } }));
  };

  // When TTS provider changes, reset config to defaults
  const handleTtsProviderChange = (provider) => {
    const defaults = {};
    (TTS_PROVIDERS[provider]?.fields || []).forEach((f) => {
      if (f.default !== undefined) defaults[f.key] = f.default;
    });
    setForm((prev) => ({ ...prev, tts_provider: provider, tts_config: defaults }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentWorkspace) { setError("No workspace selected"); return; }
    // Name + prompt instructions are mandatory (trimmed so whitespace-only fails).
    if (!form.name.trim()) { setError("Persona name is required"); return; }
    if (!form.prompt_instructions.trim()) { setError("Prompt instructions are required"); return; }
    setLoading(true);
    setError(null);
    try {
      // Build settings: merge behavior + advanced settings + voice config
      const settings = {};
      const BEHAVIOR_KEYS = ["interruption_level", "voice_clarity_preset", "network_simulation_preset", "speed", "background_sound", "background_sound_volume"];
      const NESTED_KEYS = [];
      BEHAVIOR_KEYS.forEach((k) => { if (form.settings[k] !== undefined && form.settings[k] !== "" && form.settings[k] !== null) settings[k] = form.settings[k]; });
      // A volume only means something with a sound; unset = the platform default.
      if (!settings.background_sound) delete settings.background_sound_volume;
      NESTED_KEYS.forEach((k) => { if (form.settings[k]) settings[k] = form.settings[k]; });

      // Voice mode: if custom, store tts_provider + tts_config in settings
      if (form.voice_mode === "custom") {
        settings.tts_provider = form.tts_provider;
        settings.tts_config = form.tts_config;
      }

      const payload = {
        name: form.name,
        description: form.description,
        prompt_instructions: form.prompt_instructions,
        language_id: form.language_id || null,
        is_custom: true,
        settings,
      };

      if (editId) {
        await personaAPI.updatePersona(editId, payload, currentWorkspace.id);
      } else {
        const created = await personaAPI.createPersona(payload, currentWorkspace.id);
        posthog?.capture('voice_persona_created', { persona_id: created?.data?.id });
      }
      navigate("/live-simulations/persona");
    } catch (err) {
      setError(err.response?.data?.errors
        ? (Array.isArray(err.response.data.errors) ? err.response.data.errors.join(", ") : JSON.stringify(err.response.data.errors))
        : err.response?.data?.error || "Failed to save persona");
    } finally {
      setLoading(false);
    }
  };

  if (loadingPersona) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black" />
      </div>
    );
  }

  const ttsProviderDef = TTS_PROVIDERS[form.tts_provider] || {};
  const ttsFields = ttsProviderDef.fields || [];

  // 2-letter language code (e.g. "en", "hi") from the selected primary language,
  // used to filter the custom-voice dropdown.
  const selectedLang = languages.find((l) => String(l.id) === String(form.language_id));
  const langCode = (selectedLang?.language_key || "").split(/[-_]/)[0].toLowerCase();

  return (
    <div className="px-4">
      <PageHeader
        title={!isEditMode ? "Create New Persona" : editable ? "Edit Persona" : "View Persona"}
        description="Define a voice persona for your test scenarios."
        breadcrumbs={[
          { label: "Persona Library", path: "/live-simulations/persona" },
          { label: !isEditMode ? "Create Persona" : editable ? "Edit Persona" : "View Persona" },
        ]}
      />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 mt-4 text-sm">
          {error}
        </div>
      )}

      {viewOnly && (
        <div className="mb-4 p-3 bg-blue-50 text-blue-700 text-sm rounded-lg flex items-center gap-2">
          <Eye className="h-4 w-4 flex-shrink-0" />
          {!canWrite
            ? "View only — your role doesn't have write access"
            : "View only — predefined personas are managed centrally and can't be edited"}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6 mt-6">
        {/* Section 1: Persona Information */}
        <div className="bg-card border border-border rounded-xl p-4">
          <h2 className="text-sm font-semibold text-foreground mb-1">Persona Information</h2>
          <p className="text-xs text-muted-foreground mb-5">Basic details for your voice persona.</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">Name<span className="text-red-500 ml-0.5">*</span></label>
              <input type="text" name="name" value={form.name} onChange={handleChange} disabled={!editable} className={INPUT_CLS} required placeholder="e.g. Frustrated Customer" />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">Trait (one line)</label>
              <input type="text" name="description" value={form.description} onChange={handleChange} disabled={!editable} className={INPUT_CLS} required placeholder="e.g. Impatient, speaks quickly, demands answers" />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">Primary Language</label>
              <select name="language_id" value={form.language_id} onChange={handleChange} disabled={!editable} className={SELECT_CLS}>
                {!form.language_id && <option value="">Select language</option>}
                {languages.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
          </div>

          <div className="mt-5">
            <label className="block text-xs font-medium text-foreground mb-1.5">Prompt Instructions<span className="text-red-500 ml-0.5">*</span></label>
            <textarea name="prompt_instructions" value={form.prompt_instructions} onChange={handleChange} disabled={!editable} className={INPUT_CLS} rows={4} required placeholder="Describe how this persona should behave during calls..." />
          </div>
        </div>

        {/* Section 2: Voice Settings */}
        <div className="bg-card border border-border rounded-xl p-4">
          <h2 className="text-sm font-semibold text-foreground mb-1">Voice Settings</h2>
          <p className="text-xs text-muted-foreground mb-5">Choose how this persona's voice is configured during calls.</p>

          {/* Default / Custom toggle */}
          <div className="flex gap-3 mb-5">
            <button type="button"
              onClick={() => setForm((f) => ({ ...f, voice_mode: "default" }))}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                form.voice_mode === "default"
                  ? "bg-gray-900 text-white border-gray-900"
                  : "bg-card text-foreground border-input hover:bg-muted"
              }`}
            >
              Default (from Agent)
            </button>
            <button type="button"
              onClick={() => setForm((f) => ({ ...f, voice_mode: "custom" }))}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                form.voice_mode === "custom"
                  ? "bg-gray-900 text-white border-gray-900"
                  : "bg-card text-foreground border-input hover:bg-muted"
              }`}
            >
              Custom Voice
            </button>
          </div>

          {form.voice_mode === "default" ? (
            <div className="flex items-center gap-3 px-4 py-3 bg-muted rounded-lg border border-border">
              <div className="h-8 w-8 bg-muted rounded-full flex items-center justify-center">
                <span className="text-muted-foreground text-xs font-bold">A</span>
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">Inherits from Agent</p>
                <p className="text-xs text-muted-foreground">The TTS provider, model, and voice will be taken from the agent's model settings at call time.</p>
              </div>
            </div>
          ) : (
            <div className="border border-border rounded-xl p-4 space-y-4">
              {/* Provider selector */}
              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5">TTS Provider</label>
                <select value={form.tts_provider} onChange={(e) => handleTtsProviderChange(e.target.value)} className={SELECT_CLS}>
                  {Object.entries(TTS_PROVIDERS).map(([key, def]) => (
                    <option key={key} value={key}>{def.label || key}</option>
                  ))}
                </select>
              </div>

              {/* Dynamic fields from providerCatalogue */}
              {ttsFields.map((field) => (
                <div key={field.key}>
                  <label className="block text-xs font-medium text-foreground mb-1.5">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-0.5">*</span>}
                  </label>
                  {field.description && field.type !== "boolean" && (
                    <p className="text-xs text-muted-foreground mb-1.5">{field.description}</p>
                  )}
                  <ConfigField
                    field={field}
                    value={form.tts_config[field.key]}
                    onChange={(val) => handleTtsConfigChange(field.key, val)}
                    voiceCatalog={voiceCatalogs[field.api_source]}
                    onLoadVoiceCatalog={loadVoiceCatalog}
                    langCode={langCode}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Section 3: Voice settings (collapsible — voice-only acoustic controls) */}
        <div className="bg-card border border-border rounded-xl">
          <button type="button" className="w-full flex items-center justify-between p-4 text-left"
            onClick={() => setIsVoiceSettingsOpen((o) => !o)}>
            <div>
              <h2 className="text-sm font-semibold text-foreground">Voice settings</h2>
              <p className="text-xs text-muted-foreground mt-0.5">These only affect voice runs — ignored for text.</p>
            </div>
            {isVoiceSettingsOpen ? (
              <ChevronUp className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            )}
          </button>
          {isVoiceSettingsOpen && (
            <div className="px-6 pb-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1.5">Interruption Level</label>
                  <select value={form.settings.interruption_level || ""} onChange={(e) => handleSettingChange("interruption_level", e.target.value)} className={SELECT_CLS}>
                    {INTERRUPTION_LEVEL_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1.5">Voice Clarity</label>
                  <select value={form.settings.voice_clarity_preset || ""} onChange={(e) => handleSettingChange("voice_clarity_preset", e.target.value)} className={SELECT_CLS}>
                    {VOICE_CLARITY_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1.5">Background Sound</label>
                  <select value={form.settings.background_sound || ""} onChange={(e) => handleSettingChange("background_sound", e.target.value)} className={SELECT_CLS}>
                    <option value="">None</option>
                    {backgroundNoises.map((noise) => <option key={noise.audio_url} value={noise.audio_url}>{noise.name}</option>)}
                  </select>
                </div>
              </div>

              {form.settings.background_sound && (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-xs font-medium text-foreground">Background Volume</label>
                    {form.settings.background_sound_volume != null && (
                      <button type="button" onClick={() => handleSettingChange("background_sound_volume", null)} disabled={!editable}
                        className="text-xs text-muted-foreground hover:text-foreground">
                        Reset to default
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <input type="range" min={BACKGROUND_VOLUME_MIN} max={BACKGROUND_VOLUME_MAX} step="1"
                      value={form.settings.background_sound_volume ?? BACKGROUND_VOLUME_DEFAULT}
                      onChange={(e) => handleSettingChange("background_sound_volume", parseInt(e.target.value, 10))}
                      disabled={!editable} className="flex-1 accent-gray-900" />
                    <span className="text-sm font-mono w-28 text-right text-foreground">
                      {form.settings.background_sound_volume ?? BACKGROUND_VOLUME_DEFAULT}%
                      {form.settings.background_sound_volume == null && <span className="text-muted-foreground"> (default)</span>}
                    </span>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5">Speech Speed</label>
                <div className="flex items-center gap-3">
                  <input type="range" min="0.5" max="2.0" step="0.1" value={form.settings.speed || 1.0}
                    onChange={(e) => handleSettingChange("speed", parseFloat(e.target.value))}
                    className="flex-1 accent-gray-900" />
                  <span className="text-sm font-mono w-12 text-right text-foreground">{form.settings.speed || 1.0}x</span>
                </div>
              </div>

              {/* Network Simulation */}
              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5">Network Simulation</label>
                <select value={form.settings.network_simulation_preset || ""} onChange={(e) => handleSettingChange("network_simulation_preset", e.target.value)} className={SELECT_CLS}>
                  {NETWORK_SIMULATION_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Submit */}
        {editable && (
          <div className="flex justify-end pb-6">
            <button type="submit" disabled={loading}
              className="px-6 py-2.5 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {loading ? "Saving..." : isEditMode ? "Update Persona" : "Create Persona"}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default CreatePersona;
